<template>
  <div class="detail-panel">
    <!-- Encabezado fijo -->
    <div class="detail-panel__header">
      <div class="detail-panel__header-top">
        <div class="detail-panel__nav-arrows">
          <button class="detail-panel__nav-btn" :disabled="!hasPrev" @click="navigate(-1)">
            <span class="mdi mdi-chevron-up"></span>
          </button>
          <button class="detail-panel__nav-btn" :disabled="!hasNext" @click="navigate(1)">
            <span class="mdi mdi-chevron-down"></span>
          </button>
        </div>
        <span class="detail-panel__contract-number">{{ contractNumber }}</span>
        <button class="detail-panel__close-btn" @click="$emit('close')">
          <span class="mdi mdi-close"></span>
        </button>
      </div>

      <div v-if="detail">
        <div class="detail-panel__tenant-name">{{ detail.tenant.name }}</div>
        <div class="detail-panel__id-line">
          <span class="detail-panel__id-tag">{{ detail.plot.name }}</span>
          <span v-if="detail.plot.areaM2">{{ formatNumber(detail.plot.areaM2) }} m²</span>
        </div>

        <div class="detail-panel__status-wrap">
          <button
            class="detail-panel__status-pill"
            :class="`detail-panel__status-pill--${detail.status.toLowerCase()}`"
            @click="statusMenuOpen = !statusMenuOpen"
          >
            {{ statusLabel(detail.status) }}
            <span class="mdi mdi-chevron-down"></span>
          </button>
          <div v-if="statusMenuOpen" class="detail-panel__status-menu">
            <button
              v-for="opt in STATUS_OPTIONS"
              :key="opt.value"
              class="detail-panel__status-option"
              @click="onChangeStatus(opt.value)"
            >
              {{ opt.label }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Navegación lateral de puntos -->
    <div class="detail-panel__dots">
      <button
        v-for="(block, i) in BLOCKS"
        :key="block.key"
        class="detail-panel__dot"
        :class="{ 'detail-panel__dot--active': activeBlockIndex === i }"
        @click="scrollToBlock(i)"
      ></button>
    </div>

    <!-- Contenido con scroll -->
    <div v-if="loading" class="detail-panel__loading">Cargando...</div>

    <div v-else-if="detail" class="detail-panel__content" ref="contentEl" @scroll="onContentScroll">
      <!-- Bloque 1: Condiciones económicas -->
      <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 0)" data-block="0">
        <div class="detail-panel__block-header">
          <span class="detail-panel__block-title">Condiciones del contrato</span>
          <button class="detail-panel__block-action" @click="editingConditions = !editingConditions">
            {{ editingConditions ? 'Cerrar' : 'Editar' }}
          </button>
        </div>

        <div v-if="!editingConditions" class="detail-panel__grid">
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Renta mensual</span>
            <span class="detail-panel__field-value detail-panel__field-value--mono">
              <span class="detail-panel__field-currency">{{ detail.conditions.currency }}</span>
              {{ formatNumber(detail.conditions.monthlyRent) }}
            </span>
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Día de pago acordado</span>
            <span class="detail-panel__field-value">
              Día {{ detail.conditions.effectivePaymentDay }} de cada mes
              <span v-if="!detail.conditions.paymentDay" class="detail-panel__field-hint">predeterminado de Configuración</span>
              <span v-else-if="detail.conditions.effectivePaymentDay > 28" class="detail-panel__field-hint">último día del mes si el mes es más corto</span>
            </span>
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Depósito en garantía</span>
            <span class="detail-panel__field-value detail-panel__field-value--mono">
              {{ formatCurrency(detail.conditions.depositAmount) }}
              <span
                v-if="detail.conditions.depositStatus"
                class="detail-panel__tag"
                :class="detail.conditions.depositStatus === 'HELD' ? 'detail-panel__tag--green' : 'detail-panel__tag--amber'"
              >
                {{ detail.conditions.depositStatus === 'HELD' ? 'Recibido' : 'Pendiente' }}
              </span>
            </span>
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Tipo de contrato</span>
            <span class="detail-panel__field-value">{{ contractTypeLabel(detail.conditions.contractType) }}</span>
          </div>
        </div>

        <div v-else class="detail-panel__grid">
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Renta mensual</span>
            <input v-model.number="editForm.monthlyRent" type="number" class="detail-panel__input" @keyup.enter="saveConditions" @blur="saveConditions" />
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Día de pago</span>
            <input v-model.number="editForm.paymentDay" type="number" min="1" max="31" :placeholder="`Predeterminado: día ${detail.conditions.defaultPaymentDay}`" class="detail-panel__input" @keyup.enter="saveConditions" @blur="saveConditions" />
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Depósito en garantía</span>
            <input v-model.number="editForm.depositAmount" type="number" class="detail-panel__input" @keyup.enter="saveConditions" @blur="saveConditions" />
          </div>
          <div class="detail-panel__field">
            <span class="detail-panel__field-label">Tipo de contrato</span>
            <select v-model="editForm.contractType" class="detail-panel__input" @change="saveConditions">
              <option value="FIXED">Renta fija</option>
              <option value="VARIABLE">Renta variable</option>
              <option value="MIXED">Mixto</option>
            </select>
          </div>
          <span v-if="savedFlash" class="detail-panel__saved-flash">✓ Guardado</span>
        </div>

        <div v-if="detail.conditions.escalations.length" class="detail-panel__escalations">
          <span class="detail-panel__subtitle">Escalaciones</span>
          <div
            v-for="(e, i) in detail.conditions.escalations"
            :key="i"
            class="detail-panel__escalation-row"
            :class="{ 'detail-panel__escalation-row--next': isNextEscalation(e) }"
          >
            <span v-if="e.applied" class="mdi mdi-check-circle detail-panel__escalation-check"></span>
            <span class="detail-panel__escalation-date">{{ formatDate(e.date) }}</span>
            <span class="detail-panel__escalation-percent">+{{ e.percentIncrease.toFixed(1) }}%</span>
            <span class="detail-panel__escalation-rent">{{ formatCurrency(e.newRent) }}</span>
            <span v-if="isNextEscalation(e)" class="mdi mdi-clock-outline detail-panel__escalation-clock"></span>
          </div>
        </div>
      </section>

      <!-- Bloque 2: Vigencia y renovación -->
     <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 1)" data-block="1">
  <div class="detail-panel__block-header">
    <span class="detail-panel__block-title">Vigencia</span>
  </div>

  <div class="detail-panel__vigencia-dates">
    <div>
      <span class="detail-panel__vigencia-label">Inicio</span>
      <div class="detail-panel__vigencia-date">{{ formatDate(detail.vigencia.startDate) }}</div>
    </div>
    <div class="detail-panel__vigencia-end-wrap">
      <span class="detail-panel__vigencia-label">Vencimiento</span>
      <div class="detail-panel__vigencia-date" :class="`detail-panel__vigencia-date--${vigenciaColor}`">
        {{ formatDate(detail.vigencia.endDate) }}
      </div>
    </div>
  </div>

  <div class="detail-panel__progress-wrap">
    <div class="detail-panel__progress-bg">
      <div class="detail-panel__progress-fill" :class="`detail-panel__progress-fill--${vigenciaColor}`" :style="{ width: detail.vigencia.progressPercent + '%' }"></div>
    </div>
  </div>
  <div class="detail-panel__vigencia-text">{{ vigenciaText }}</div>

  <button
    v-if="detail.vigencia.daysRemaining <= 90 && !detail.vigencia.activeProcess"
    class="detail-panel__renewal-btn"
    @click="startRenewal"
  >
    Iniciar proceso de renovación
  </button>

  <div v-else-if="detail.vigencia.activeProcess === 'RENEWAL'" class="detail-panel__renewal-block">
    <div class="detail-panel__renewal-stepper">
      <div
        v-for="(step, i) in RENEWAL_STEPS"
        :key="step.value"
        class="detail-panel__renewal-step"
        :class="{
          'detail-panel__renewal-step--current': detail.vigencia.renewalStage === step.value,
          'detail-panel__renewal-step--done': renewalStepIndex(detail.vigencia.renewalStage) > i,
        }"
      >
        {{ step.label }}
      </div>
    </div>

    <button
      v-if="detail.vigencia.renewalStage !== 'SIGNED'"
      class="detail-panel__renewal-advance-btn"
      @click="advanceRenewalStage"
    >
      Avanzar a la siguiente etapa
    </button>

    <template v-else>
      <button v-if="!showConfirmRenewal" class="detail-panel__renewal-btn" @click="showConfirmRenewal = true">
        Confirmar renovación
      </button>
      <div v-else class="detail-panel__renewal-confirm-form">
        <input v-model="renewalForm.newEndDate" type="date" class="detail-panel__input" />
        <input v-model.number="renewalForm.newRent" type="number" placeholder="Nueva renta (opcional)" class="detail-panel__input" />
        <button class="detail-panel__form-submit" @click="submitConfirmRenewal">Confirmar</button>
        <button class="detail-panel__form-cancel" @click="showConfirmRenewal = false">Cancelar</button>
      </div>
    </template>
  </div>
</section>

      <!-- Bloque 3: Historial de pagos -->
      <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 2)" data-block="2">
        <div class="detail-panel__block-header">
          <span class="detail-panel__block-title">Historial de pagos</span>
        </div>

       <div class="detail-panel__payments-summary">
                <div>
                    <span class="detail-panel__summary-number detail-panel__summary-number--green">{{ detail.payments.onTimeCount }}</span>
                    <span class="detail-panel__summary-label">Pagos al corriente</span>
                </div>
                <div>
                    <span class="detail-panel__summary-number" :class="detail.payments.partialCount > 0 ? 'detail-panel__summary-number--amber' : 'detail-panel__summary-number--gray'">
                    {{ detail.payments.partialCount }}
                    </span>
                    <span class="detail-panel__summary-label">Pagos parciales</span>
                </div>
                <div>
                    <span class="detail-panel__summary-number" :class="detail.payments.overdueCount > 0 ? 'detail-panel__summary-number--red' : 'detail-panel__summary-number--gray'">
                    {{ detail.payments.overdueCount }}
                    </span>
                    <span class="detail-panel__summary-label">Pagos vencidos</span>
                </div>
                </div>

        <div class="detail-panel__payments-list">
        <div v-for="p in detail.payments.history" :key="p.id" class="detail-panel__payment-row">
            <span class="detail-panel__payment-month">{{ formatMonth(p.dueDate) }}</span>
            <span class="detail-panel__payment-amount">
            {{ p.status === 'PARTIAL' ? `${formatCurrency(p.amountPaid)} de ${formatCurrency(p.amount)}` : formatCurrency(p.amount) }}
            </span>
            <span class="mdi detail-panel__payment-icon" :class="paymentIcon(p.status)"></span>
        </div>
        </div>

        <ContractHistoryBlock :contract-id="contractId" @changed="reloadDetailQuiet" />
      </section>

      <!-- Bloque 4: Contactos -->
     <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 3)" data-block="3">
  <div class="detail-panel__block-header">
    <span class="detail-panel__block-title">Contactos</span>
    <button class="detail-panel__block-action" @click="showAddContact = !showAddContact">Agregar contacto</button>
  </div>

  <div v-if="showAddContact" class="detail-panel__add-form">
    <input v-model="contactForm.name" placeholder="Nombre" class="detail-panel__input" />
    <input v-model="contactForm.role" placeholder="Cargo" class="detail-panel__input" />
    <select v-model="contactForm.roleType" class="detail-panel__input">
      <option value="LEGAL">Representante legal</option>
      <option value="OPERATIONAL">Operativo</option>
      <option value="PAYMENTS">Pagos</option>
      <option value="EMERGENCY">Emergencia</option>
    </select>
    <input v-model="contactForm.phone" placeholder="Teléfono" class="detail-panel__input" />
    <input v-model="contactForm.email" placeholder="Correo" class="detail-panel__input" />
    <button class="detail-panel__form-submit" @click="submitContact">Guardar</button>
  </div>

  <div class="detail-panel__contacts-list">
    <div v-for="c in detail.contacts" :key="c.id" class="detail-panel__contact-card">
      <span class="detail-panel__contact-role-tag" :class="`detail-panel__contact-role-tag--${c.roleType.toLowerCase()}`">
        {{ contactRoleLabel(c.roleType) }}
      </span>

      <template v-if="editingContactId !== c.id">
        <div class="detail-panel__contact-name">{{ c.name }}</div>
        <div v-if="c.role" class="detail-panel__contact-role">{{ c.role }}</div>
        <div class="detail-panel__contact-line" v-if="c.phone">
          <span class="mdi mdi-phone-outline"></span>
          {{ c.phone }}
          <span class="mdi mdi-content-copy detail-panel__copy-icon" @click="copyToClipboard(c.phone)"></span>
        </div>
        <div class="detail-panel__contact-line" v-if="c.email">
          <span class="mdi mdi-email-outline"></span>
          {{ c.email }}
          <span class="mdi mdi-content-copy detail-panel__copy-icon" @click="copyToClipboard(c.email)"></span>
        </div>
        <div v-if="c.notes" class="detail-panel__contact-notes">{{ c.notes }}</div>

        <div class="detail-panel__contact-actions">
          <button class="detail-panel__contact-action-btn" @click="startEditContact(c)">Editar</button>
          <button class="detail-panel__contact-action-btn detail-panel__contact-action-btn--danger" @click="onDeleteContact(c.id)">Eliminar</button>
        </div>
      </template>

      <div v-else class="detail-panel__add-form">
        <input v-model="editContactForm.name" placeholder="Nombre" class="detail-panel__input" />
        <input v-model="editContactForm.role" placeholder="Cargo" class="detail-panel__input" />
        <select v-model="editContactForm.roleType" class="detail-panel__input">
          <option value="LEGAL">Representante legal</option>
          <option value="OPERATIONAL">Operativo</option>
          <option value="PAYMENTS">Pagos</option>
          <option value="EMERGENCY">Emergencia</option>
        </select>
        <input v-model="editContactForm.phone" placeholder="Teléfono" class="detail-panel__input" />
        <input v-model="editContactForm.email" placeholder="Correo" class="detail-panel__input" />
        <input v-model="editContactForm.notes" placeholder="Notas" class="detail-panel__input" />
        <button class="detail-panel__form-submit" @click="submitEditContact">Guardar</button>
        <button class="detail-panel__form-cancel" @click="editingContactId = null">Cancelar</button>
      </div>
    </div>
  </div>
</section>

      <!-- Bloque 5: Documentos -->
<section class="detail-panel__block" :ref="(el) => setBlockRef(el, 4)" data-block="4">
  <div class="detail-panel__block-header">
    <span class="detail-panel__block-title">Documentos</span>
    <button class="detail-panel__block-action" @click="fileInputEl?.click()">Subir documento</button>
    <input ref="fileInputEl" type="file" accept="application/pdf,image/*" style="display: none" @change="onFileSelected" />
  </div>

  <div v-if="!detail.documents.length" class="detail-panel__dropzone" @click="fileInputEl?.click()">
    <span class="mdi mdi-cloud-upload-outline"></span>
    <span>Arrastra archivos aquí o haz clic para seleccionar</span>
  </div>

  <div class="detail-panel__documents-list">
    <div v-for="d in detail.documents" :key="d.id" class="detail-panel__document-row">
      <span class="mdi mdi-file-pdf-box detail-panel__document-icon"></span>
      <div class="detail-panel__document-main">
        <div class="detail-panel__document-name">{{ d.fileName }}</div>
        <span class="detail-panel__document-tag">{{ documentTypeLabel(d.type) }}</span>
      </div>
      <span class="detail-panel__document-date">{{ formatDate(d.uploadedAt) }}</span>
      <a v-if="d.filePath" :href="getDocumentUrl(d.filePath)" target="_blank" class="mdi mdi-eye-outline detail-panel__document-action"></a>
      <a v-if="d.filePath" :href="getDocumentUrl(d.filePath)" :download="d.fileName" class="mdi mdi-download detail-panel__document-action"></a>
      <button
        class="mdi mdi-trash-can-outline detail-panel__document-action detail-panel__document-action--danger"
        title="Eliminar documento"
        @click="onDeleteDocument(d.id)"
      ></button>
    </div>
  </div>
</section>
      <!-- Bloque 6: Notas internas -->
      <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 5)" data-block="5">
        <div class="detail-panel__block-header">
          <span class="detail-panel__block-title">Notas internas</span>
          <span class="mdi mdi-lock-outline detail-panel__lock-icon"></span>
        </div>

        <div class="detail-panel__notes-list">
          <div v-for="n in detail.notes" :key="n.id" class="detail-panel__note-bubble">
            <div class="detail-panel__note-text">{{ n.text }}</div>
            <div class="detail-panel__note-meta">{{ n.author }} · {{ formatDateTime(n.createdAt) }}</div>
          </div>
        </div>

        <textarea
          v-model="noteText"
          class="detail-panel__note-input"
          placeholder="Agrega una nota interna..."
          rows="1"
          @focus="noteFocused = true"
        ></textarea>
        <div v-if="noteFocused" class="detail-panel__note-actions">
          <button class="detail-panel__note-save" @click="submitNote">Guardar nota</button>
        </div>
      </section>

      <!-- Bloque 7: Log de auditoría -->
      <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 6)" data-block="6">
        <div class="detail-panel__block-header">
          <span class="detail-panel__block-title">Historial de cambios</span>
          <button class="detail-panel__block-action" @click="auditExpanded = !auditExpanded">
            {{ auditExpanded ? 'Ocultar' : 'Mostrar' }}
          </button>
        </div>

        <div v-if="auditExpanded" class="detail-panel__audit-list">
          <div v-for="a in detail.auditLog" :key="a.id" class="detail-panel__audit-row" :class="{ 'detail-panel__audit-row--significant': a.significant }">
            <span class="mdi detail-panel__audit-icon" :class="auditIcon(a.eventType)"></span>
            <span class="detail-panel__audit-text">{{ a.text }}</span>
            <span class="detail-panel__audit-meta">{{ a.author }} · {{ formatDateTime(a.createdAt) }}</span>
          </div>
          <div v-if="!detail.auditLog.length" class="detail-panel__audit-empty">Sin eventos registrados todavía</div>
        </div>
      </section>

      <!-- Bloque 8: Acceso al portal del inquilino -->
      <section class="detail-panel__block" :ref="(el) => setBlockRef(el, 7)" data-block="7">
        <div class="detail-panel__block-header">
          <span class="detail-panel__block-title">Acceso al portal del inquilino</span>
        </div>
        <div class="detail-panel__portal-row">
          <div class="detail-panel__portal-text">
            <span class="detail-panel__portal-title">Acceso activo al portal</span>
            <span class="detail-panel__portal-desc">
              El inquilino podrá consultar su contrato, estados de cuenta y tickets desde el portal de inquilinos.
            </span>
          </div>
          <label class="detail-panel__portal-toggle">
            <input
              type="checkbox"
              :checked="detail.tenantPortalAccessEnabled"
              @change="onTogglePortalAccess($event.target.checked)"
            />
            <span class="detail-panel__portal-toggle-slider"></span>
          </label>
        </div>

        <!-- Cuenta del inquilino en el portal -->
        <div v-if="detail.tenantPortalAccessEnabled || portalAccess?.account" class="portal-acct">
          <p v-if="portalAccess && !portalAccess.parkPortalEnabled" class="portal-acct__warn">
            El portal está desactivado para el parque: actívalo en Configuración → Usuarios para que el inquilino pueda entrar.
          </p>

          <template v-if="portalAccess?.account">
            <div class="portal-acct__row">
              <div class="portal-acct__who">
                <span class="portal-acct__email">{{ portalAccess.account.email }}</span>
                <span class="portal-acct__meta">{{ accountMeta }}</span>
              </div>
              <span class="portal-acct__pill" :class="`portal-acct__pill--${portalAccess.account.status.toLowerCase()}`">{{ ACCOUNT_STATUS[portalAccess.account.status] }}</span>
            </div>
            <div class="portal-acct__actions">
              <button v-if="portalAccess.account.status === 'PENDING'" class="portal-acct__btn" :disabled="portalBusy" @click="generateInvite(portalAccess.account.email, portalAccess.account.name)">Generar nuevo link</button>
              <button v-if="portalAccess.account.status === 'ACTIVE'" class="portal-acct__btn" :disabled="portalBusy" @click="toggleSuspended(true)">Suspender acceso</button>
              <button v-if="portalAccess.account.status === 'SUSPENDED'" class="portal-acct__btn" :disabled="portalBusy" @click="toggleSuspended(false)">Reactivar acceso</button>
            </div>
          </template>

          <template v-else-if="portalAccess">
            <label class="portal-acct__label" for="portal-invite-email">Correo del contacto que usará el portal</label>
            <input id="portal-invite-email" v-model="inviteEmail" class="portal-acct__input" type="email" list="portal-invite-options" placeholder="correo@empresa.com" />
            <datalist id="portal-invite-options">
              <option v-for="o in portalAccess.contactOptions" :key="o.email" :value="o.email">{{ o.name }}</option>
            </datalist>
            <div class="portal-acct__actions">
              <button class="portal-acct__btn portal-acct__btn--primary" :disabled="portalBusy || !inviteEmail" @click="generateInvite(inviteEmail, null)">Generar invitación</button>
            </div>
          </template>

          <p v-if="portalError" class="portal-acct__error">{{ portalError }}</p>

          <div v-if="inviteLink" class="portal-acct__link">
            <span class="portal-acct__label">Enviamos la invitación por correo (vence en 72 horas). Si no le llega, comparte este link.</span>
            <div class="portal-acct__link-row">
              <input class="portal-acct__input" :value="inviteLink" readonly @focus="$event.target.select()" />
              <button class="portal-acct__btn" @click="copyInvite">{{ inviteCopied ? 'Copiado' : 'Copiar' }}</button>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- Barra de acciones inferior -->
    <div v-if="detail" class="detail-panel__actions">
      <button class="detail-panel__action-primary" @click="showPaymentForm = !showPaymentForm">Registrar pago</button>
<button class="detail-panel__action-secondary" @click="downloadPdf('statement')">Generar estado de cuenta</button>
<div class="detail-panel__more-wrap">
  <button class="detail-panel__more-btn" @click="moreMenuOpen = !moreMenuOpen">
    <span class="mdi mdi-dots-horizontal"></span>
  </button>
  <div v-if="moreMenuOpen" class="detail-panel__more-menu">
    <button @click="startRenewal">Iniciar renovación</button>
    <button @click="editingConditions = true; moreMenuOpen = false; scrollToBlock(0)">Editar condiciones del contrato</button>
    <button @click="downloadPdf('collection-letter')">Generar carta de cobro</button>
    <button @click="onFlagIncident">Registrar incidencia</button>
    <button v-if="detail.status === 'ENDED'" @click="onReactivate">Reactivar contrato</button>
    <button v-else @click="onArchive">Archivar contrato</button>
    <button @click="downloadPdf('full-file')">Imprimir expediente completo</button>
  </div>
</div>
    </div>

   <p v-if="pdfBusy" class="detail-panel__action-error" style="color: #64748b">Generando PDF…</p>
   <p v-if="actionError" class="detail-panel__action-error">{{ actionError }}</p>

   <div v-if="showPaymentForm" class="detail-panel__payment-form">
  <div class="detail-panel__payment-form-fields">
    <input v-model.number="paymentForm.amount" type="number" placeholder="Monto" class="detail-panel__input" />
    <input v-model="paymentForm.date" type="date" class="detail-panel__input" />
  </div>
  <div class="detail-panel__payment-form-actions">
    <button class="detail-panel__form-submit" @click="submitPayment">Confirmar pago</button>
    <button class="detail-panel__form-cancel" @click="showPaymentForm = false">Cancelar</button>
  </div>
</div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import {
  getContractDetail,
  registerPayment,
  addContractNote,
  addContact,
  updateContractStatus,
  updateConditions,
  updateContractProcess,
  updatePortalAccess,
  getPortalAccount,
  invitePortalTenant,
  setPortalAccountSuspended,
} from '@/services/contracts'

import { downloadContractPdf } from '@/services/contracts'
import ContractHistoryBlock from './ContractHistoryBlock.vue'

const pdfBusy = ref(false)

async function downloadPdf(type) {
  moreMenuOpen.value = false
  if (pdfBusy.value) return
  pdfBusy.value = true
  actionError.value = ''
  try {
    await downloadContractPdf(props.contractId, type)
  } catch (err) {
    actionError.value = err.message
    setTimeout(() => (actionError.value = ''), 6000)
  } finally {
    pdfBusy.value = false
  }
}

const props = defineProps({
  contractId: { type: String, required: true },
  contractList: { type: Array, default: () => [] },
  pendingAction: { type: String, default: null },
})

const emit = defineEmits(['close', 'navigate', 'refresh', 'action-handled'])

import { getDocumentUrl, uploadContractDocument, deleteContractDocument, updateContact, deleteContact, confirmRenewal } from '@/services/contracts'
// --- Renovación: avanzar etapa y confirmar ---
const showConfirmRenewal = ref(false)
const renewalForm = ref({ newEndDate: '', newRent: null })

async function advanceRenewalStage() {
  const idx = renewalStepIndex(detail.value.vigencia.renewalStage)
  const next = RENEWAL_STEPS[idx + 1]
  if (!next) return
  await updateContractProcess(props.contractId, { activeProcess: 'RENEWAL', renewalStage: next.value })
  await loadDetail()
  emit('refresh')
}

async function submitConfirmRenewal() {
  if (!renewalForm.value.newEndDate) return
  await confirmRenewal(props.contractId, renewalForm.value)
  showConfirmRenewal.value = false
  renewalForm.value = { newEndDate: '', newRent: null }
  await loadDetail()
  emit('refresh')
}

// --- Contactos: editar y eliminar ---
const editingContactId = ref(null)
const editContactForm = ref({ name: '', role: '', roleType: 'OPERATIONAL', phone: '', email: '', notes: '' })

function startEditContact(c) {
  editingContactId.value = c.id
  editContactForm.value = { name: c.name, role: c.role || '', roleType: c.roleType, phone: c.phone || '', email: c.email || '', notes: c.notes || '' }
}

async function submitEditContact() {
  await updateContact(editingContactId.value, editContactForm.value)
  editingContactId.value = null
  await loadDetail()
}

async function onDeleteContact(contactId) {
  if (!confirm('¿Eliminar este contacto?')) return
  await deleteContact(contactId)
  await loadDetail()
}



// --- Documentos: subida real ---
const fileInputEl = ref(null)

async function onFileSelected(event) {
  const file = event.target.files[0]
  if (!file) return
  await uploadContractDocument(props.contractId, file, 'OTHER')
  event.target.value = ''
  await loadDetail()
}

async function onDeleteDocument(documentId) {
  if (!confirm('¿Eliminar este documento? Esta acción no se puede deshacer.')) return
  await deleteContractDocument(documentId)
  await loadDetail()
}

const BLOCKS = [
  { key: 'conditions' }, { key: 'vigencia' }, { key: 'payments' },
  { key: 'contacts' }, { key: 'documents' }, { key: 'notes' }, { key: 'audit' }, { key: 'portal' },
]

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Activo' },
  { value: 'PENDING', label: 'En negociación' },
  { value: 'ENDED', label: 'Terminado' },
]

const RENEWAL_STEPS = [
  { value: 'INITIATED', label: 'Iniciada' },
  { value: 'NEGOTIATION', label: 'En negociación' },
  { value: 'DOCUMENTS', label: 'Documentos' },
  { value: 'SIGNED', label: 'Firmada' },
]


const detail = ref(null)
const loading = ref(true)
const statusMenuOpen = ref(false)
const moreMenuOpen = ref(false)
const editingConditions = ref(false)
const savedFlash = ref(false)
const showAddContact = ref(false)
const auditExpanded = ref(false)
const showPaymentForm = ref(false)
const noteFocused = ref(false)
const noteText = ref('')

const editForm = ref({ monthlyRent: 0, paymentDay: null, depositAmount: null, contractType: 'FIXED' })
const contactForm = ref({ name: '', role: '', roleType: 'OPERATIONAL', phone: '', email: '' })
const paymentForm = ref({ amount: null, date: new Date().toISOString().slice(0, 10) })

// Refresca el detalle sin mostrar el estado de carga (se usa tras corregir el historial).
async function reloadDetailQuiet() {
  try { detail.value = await getContractDetail(props.contractId) } catch { /* se conserva lo que ya se muestra */ }
}

async function loadDetail() {
  loading.value = true
  try {
    detail.value = await getContractDetail(props.contractId)
    if (detail.value) {
      loadPortalAccess()
      editForm.value = {
        monthlyRent: detail.value.conditions.monthlyRent,
        paymentDay: detail.value.conditions.paymentDay,
        depositAmount: detail.value.conditions.depositAmount,
        contractType: detail.value.conditions.contractType,
      }
    }
  } finally {
    loading.value = false
  }
}


// --- Navegación entre contratos ---
const currentIndex = computed(() => props.contractList.findIndex((c) => c.id === props.contractId))
const hasPrev = computed(() => currentIndex.value > 0)
const hasNext = computed(() => currentIndex.value >= 0 && currentIndex.value < props.contractList.length - 1)

function navigate(delta) {
  const next = props.contractList[currentIndex.value + delta]
  if (next) emit('navigate', next.id)
}

const contractNumber = computed(() => `CNT-${props.contractId.slice(0, 8).toUpperCase()}`)

// --- Estatus ---
function statusLabel(s) {
  return STATUS_OPTIONS.find((o) => o.value === s)?.label || s
}

async function onChangeStatus(status) {
  statusMenuOpen.value = false
  if (status === 'ENDED' && detail.value.status !== 'ENDED') {
    if (!confirm('¿Marcar este contrato como terminado?')) return
  }
  await changeStatus(status)
}

// --- Condiciones económicas ---
async function saveConditions() {
  // Vacío = usar el día predeterminado de Configuración.
  const day = editForm.value.paymentDay
  await updateConditions(props.contractId, { ...editForm.value, paymentDay: day === '' || day === null || day === undefined ? null : day })
  await loadDetail()
  emit('refresh')
  savedFlash.value = true
  setTimeout(() => (savedFlash.value = false), 1000)
}

async function onTogglePortalAccess(enabled) {
  await updatePortalAccess(props.contractId, enabled)
  await loadDetail()
}

// --- Cuenta del inquilino en el portal ---
const portalAccess = ref(null)
const inviteEmail = ref('')
const inviteLink = ref('')
const inviteCopied = ref(false)
const portalBusy = ref(false)
const portalError = ref('')
const ACCOUNT_STATUS = { PENDING: 'Invitación pendiente', ACTIVE: 'Activa', SUSPENDED: 'Suspendida' }
const PORTAL_ERRORS = {
  INVALID_EMAIL: 'Escribe un correo válido.',
  EMAIL_BELONGS_TO_STAFF: 'Ese correo ya pertenece a una cuenta del equipo del parque. Usa otro.',
  EMAIL_IN_USE: 'Ese correo ya está en uso por otro inquilino.',
  ALREADY_ACTIVATED: 'Este inquilino ya activó su cuenta.',
}

const accountMeta = computed(() => {
  const a = portalAccess.value?.account
  if (!a) return ''
  if (a.status === 'ACTIVE') return a.lastLoginAt ? `Último acceso: ${new Date(a.lastLoginAt).toLocaleDateString('es-MX')}` : 'Aún no ha ingresado'
  if (a.status === 'PENDING') return 'Aún no activa su cuenta'
  return 'Sin acceso'
})

async function loadPortalAccess() {
  try {
    portalAccess.value = await getPortalAccount(props.contractId)
  } catch {
    portalAccess.value = null
  }
}

async function generateInvite(email, name) {
  portalBusy.value = true
  portalError.value = ''
  inviteCopied.value = false
  try {
    const result = await invitePortalTenant(props.contractId, { email, name })
    inviteLink.value = result.inviteUrl
    inviteEmail.value = ''
    await loadPortalAccess()
    await loadDetail()
  } catch (err) {
    portalError.value = PORTAL_ERRORS[err.message] || 'No se pudo generar la invitación.'
  } finally {
    portalBusy.value = false
  }
}

async function toggleSuspended(suspended) {
  portalBusy.value = true
  portalError.value = ''
  try {
    await setPortalAccountSuspended(props.contractId, suspended)
    await loadPortalAccess()
  } catch {
    portalError.value = 'No se pudo actualizar la cuenta.'
  } finally {
    portalBusy.value = false
  }
}

watch(() => props.contractId, () => {
  inviteLink.value = ''
  portalError.value = ''
  inviteEmail.value = ''
})

async function copyInvite() {
  try {
    await navigator.clipboard.writeText(inviteLink.value)
    inviteCopied.value = true
    setTimeout(() => (inviteCopied.value = false), 1500)
  } catch {
    /* el campo es seleccionable: se puede copiar a mano */
  }
}

function isNextEscalation(e) {
  return !e.applied && detail.value.conditions.escalations.find((x) => !x.applied) === e
}

// --- Vigencia ---
const vigenciaColor = computed(() => {
  const d = detail.value.vigencia.daysRemaining
  if (detail.value.vigencia.activeProcess === 'RENEWAL') return 'green'
  if (d < 0) return 'red'
  if (d <= 30) return 'red'
  if (d <= 180) return 'amber'
  if (d <= 365) return 'gray'
  return 'navy'
})

const vigenciaText = computed(() => {
  const d = detail.value.vigencia.daysRemaining
  if (d < 0) return `Venció hace ${Math.abs(d)} día${Math.abs(d) === 1 ? '' : 's'}`
  return `Quedan ${d} día${d === 1 ? '' : 's'} de contrato`
})

function renewalStepIndex(stage) {
  return RENEWAL_STEPS.findIndex((s) => s.value === stage)
}

async function startRenewal() {
  moreMenuOpen.value = false
  await updateContractProcess(props.contractId, { activeProcess: 'RENEWAL', renewalStage: 'INITIATED' })
  await loadDetail()
  emit('refresh')
}

// --- Pagos ---
function paymentIcon(status) {
  if (status === 'PAID') return 'mdi-check-circle detail-panel__payment-icon--green'
  if (status === 'PARTIAL') return 'mdi-circle-half-full detail-panel__payment-icon--amber'
  if (status === 'OVERDUE') return 'mdi-close-circle detail-panel__payment-icon--red'
  return 'mdi-minus-circle-outline detail-panel__payment-icon--gray'
}

async function submitPayment() {
  if (!paymentForm.value.amount) return
  await registerPayment(props.contractId, paymentForm.value)
  showPaymentForm.value = false
  paymentForm.value = { amount: null, date: new Date().toISOString().slice(0, 10) }
  await loadDetail()
  emit('refresh')
}

// --- Contactos ---
async function submitContact() {
  if (!contactForm.value.name) return
  await addContact(props.contractId, contactForm.value)
  showAddContact.value = false
  contactForm.value = { name: '', role: '', roleType: 'OPERATIONAL', phone: '', email: '' }
  await loadDetail()
}

function contactRoleLabel(rt) {
  return { LEGAL: 'Legal', OPERATIONAL: 'Operativo', PAYMENTS: 'Pagos', EMERGENCY: 'Emergencia' }[rt] || rt
}

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text)
}

function documentTypeLabel(t) {
  return { SIGNED_CONTRACT: 'Contrato firmado', ANNEX: 'Anexo', ID: 'Identificación', OTHER: 'Otro' }[t] || t
}

// --- Notas ---
async function submitNote() {
  if (!noteText.value.trim()) return
  await addContractNote(props.contractId, noteText.value, 'Usuario')
  noteText.value = ''
  noteFocused.value = false
  await loadDetail()
}

// --- Auditoría ---
function auditIcon(type) {
  return { EDIT: 'mdi-pencil', CREATE: 'mdi-plus', STATUS_CHANGE: 'mdi-arrow-right-bold', PAYMENT: 'mdi-cash' }[type] || 'mdi-circle-small'
}

// "Registrar incidencia" abre una solicitud de mantenimiento con la nave de este contrato ya elegida.
const router = useRouter()
function onFlagIncident() {
  moreMenuOpen.value = false
  if (!detail.value?.plot) return
  router.push({ path: '/Mantenimiento', query: { nueva: '1', plotId: detail.value.plot.id, nave: detail.value.plot.name } })
}

const actionError = ref('')

async function onArchive() {
  moreMenuOpen.value = false
  if (!confirm('¿Archivar este contrato? Dejará de contar como activo. Podrás reactivarlo desde este mismo menú.')) return
  await changeStatus('ENDED')
}

async function onReactivate() {
  moreMenuOpen.value = false
  if (!confirm('¿Reactivar este contrato? Volverá a contar como activo.')) return
  await changeStatus('ACTIVE')
}

async function changeStatus(status) {
  actionError.value = ''
  try {
    await updateContractStatus(props.contractId, status)
    await loadDetail()
    emit('refresh')
  } catch (err) {
    actionError.value = err.message
    setTimeout(() => (actionError.value = ''), 6000)
  }
}

// --- Navegación de puntos + scroll activo ---
const contentEl = ref(null)
const blockRefs = ref([])
const activeBlockIndex = ref(0)

function setBlockRef(el, index) {
  if (el) blockRefs.value[index] = el
}

function scrollToBlock(i) {
  blockRefs.value[i]?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function onContentScroll() {
  if (!contentEl.value) return
  const scrollTop = contentEl.value.scrollTop
  let closest = 0
  let closestDist = Infinity
  blockRefs.value.forEach((el, i) => {
    if (!el) return
    const dist = Math.abs(el.offsetTop - scrollTop)
    if (dist < closestDist) {
      closestDist = dist
      closest = i
    }
  })
  activeBlockIndex.value = closest
}

// --- Formato ---
function formatNumber(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(n)
}

function formatCurrency(n) {
  if (n === null || n === undefined) return '—'
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 }).format(n)
}

function formatDate(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' })
}

function formatDateTime(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
}

function formatMonth(iso) {
  return new Date(iso).toLocaleDateString('es-MX', { month: 'short', year: 'numeric' })
}

function contractTypeLabel(t) {
  return { FIXED: 'Renta fija', VARIABLE: 'Renta variable', MIXED: 'Mixto' }[t] || t
}

watch(
  () => props.contractId,
  async () => {
    await loadDetail()
    await nextTick()

    if (props.pendingAction === 'start-renewal') {
      if (detail.value && !detail.value.vigencia.activeProcess) await startRenewal()
    } else if (props.pendingAction === 'scroll-vigencia') {
      scrollToBlock(1)
    } else if (props.pendingAction === 'scroll-payments') {
      scrollToBlock(2)
    }

    emit('action-handled')
  },
  { immediate: true }
)
</script>

<style scoped>
.detail-panel {
  flex: 0 0 40%;
  max-width: 40%;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid var(--border-active);
  border-radius: 10px;
  overflow: hidden;
  margin: 0 1.5rem 1.5rem 0;
  position: relative;
  min-height: 0;
}

.detail-panel__loading {
  padding: 2rem;
  text-align: center;
  color: var(--text-secondary);
  font-family: var(--font-body);
  font-size: 13px;
}

.detail-panel__header {
  flex-shrink: 0;
  padding: 0.75rem 1.25rem 0.75rem calc(1.25rem + 12px);
  border-bottom: 1px solid #f1f5f9;
}

.detail-panel__header-top {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.4rem;
}

.detail-panel__nav-arrows {
  display: flex;
  flex-direction: column;
}

.detail-panel__nav-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
  padding: 0;
  line-height: 0.7;
}

.detail-panel__nav-btn:hover:not(:disabled) {
  color: #1e3a5f;
}

.detail-panel__nav-btn:disabled {
  opacity: 0.3;
  cursor: default;
}

.detail-panel__contract-number {
  flex: 1;
  text-align: center;
  font-family: var(--font-mono);
  font-size: 11px;
  color: #94a3b8;
}

.detail-panel__close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.detail-panel__close-btn:hover {
  color: #1e3a5f;
}

.detail-panel__tenant-name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 18px;
  line-height: 1.5;
  color: #1e3a5f;
}

.detail-panel__id-line {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #64748b;
  margin-top: 0.5rem;
}

.detail-panel__id-tag {
  background: #f1f5f9;
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
  color: #64748b;
}

.detail-panel__status-wrap {
  position: relative;
  margin-top: 0.5rem;
}

.detail-panel__status-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 20px;
  padding: 0.2rem 0.6rem;
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  border: none;
  cursor: pointer;
}

.detail-panel__status-pill--active { background: #dcfce7; color: #16a34a; }
.detail-panel__status-pill--pending { background: #dbeafe; color: #3b82f6; }
.detail-panel__status-pill--ended { background: #f1f5f9; color: #64748b; }

.detail-panel__status-menu {
  position: absolute;
  top: 100%;
  left: 0;
  margin-top: 4px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  z-index: 50;
  min-width: 160px;
  padding: 0.3rem;
}

.detail-panel__status-option {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.detail-panel__status-option:hover {
  background: #f8fafc;
}

.detail-panel__dots {
  position: absolute;
  left: 4px;
  top: 90px;
  bottom: 60px;
  width: 4px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 10px;
  z-index: 10;
}

.detail-panel__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #e2e8f0;
  border: none;
  cursor: pointer;
  padding: 0;
}

.detail-panel__dot--active {
  background: #3b82f6;
}

.detail-panel__content {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-left: 12px;
}

.detail-panel__content::-webkit-scrollbar {
  width: 6px;
}

.detail-panel__content::-webkit-scrollbar-thumb {
  background: var(--border-active);
  border-radius: 3px;
}

.detail-panel__content::-webkit-scrollbar-track {
  background: transparent;
}

.detail-panel__block {
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f1f5f9;
}

.detail-panel__block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.6rem;
}

.detail-panel__block-title {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  color: #94a3b8;
}

.detail-panel__block-action {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  padding: 0;
}

.detail-panel__lock-icon {
  color: #94a3b8;
  font-size: 12px;
}

.detail-panel__portal-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.detail-panel__portal-text {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.detail-panel__portal-title {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
}

.detail-panel__portal-desc {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.detail-panel__portal-toggle {
  position: relative;
  display: inline-block;
  width: 34px;
  height: 18px;
  flex-shrink: 0;
}

.detail-panel__portal-toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.detail-panel__portal-toggle-slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 20px;
  transition: 0.15s;
}

.detail-panel__portal-toggle-slider::before {
  content: '';
  position: absolute;
  height: 14px;
  width: 14px;
  left: 2px;
  top: 2px;
  background: #fff;
  border-radius: 50%;
  transition: 0.15s;
}

.detail-panel__portal-toggle input:checked + .detail-panel__portal-toggle-slider {
  background: #3b82f6;
}

.detail-panel__portal-toggle input:checked + .detail-panel__portal-toggle-slider::before {
  transform: translateX(16px);
}

.portal-acct { margin-top: 0.75rem; padding-top: 0.75rem; border-top: 1px solid #f1f5f9; display: flex; flex-direction: column; gap: 0.5rem; }
.portal-acct__row { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.portal-acct__who { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.portal-acct__email { font: 500 13px Inter, sans-serif; color: #1e3a5f; word-break: break-all; }
.portal-acct__meta { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.portal-acct__pill { font: 500 11px Inter, sans-serif; padding: 2px 8px; border-radius: 10px; white-space: nowrap; }
.portal-acct__pill--active { background: #dcfce7; color: #15803d; }
.portal-acct__pill--pending { background: #fef3c7; color: #b45309; }
.portal-acct__pill--suspended { background: #fee2e2; color: #b91c1c; }
.portal-acct__label { font: 400 11px Inter, sans-serif; color: #94a3b8; }
.portal-acct__input { height: 32px; padding: 0 10px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 13px Inter, sans-serif; color: #1e3a5f; width: 100%; }
.portal-acct__actions { display: flex; gap: 0.5rem; }
.portal-acct__btn { height: 30px; padding: 0 12px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #1e3a5f; font: 500 12px Inter, sans-serif; cursor: pointer; white-space: nowrap; }
.portal-acct__btn:disabled { opacity: 0.5; cursor: not-allowed; }
.portal-acct__btn--primary { background: #3b82f6; border-color: #3b82f6; color: #fff; }
.portal-acct__link { display: flex; flex-direction: column; gap: 4px; padding: 8px; background: #f0fdf4; border: 1px solid #bbf7d0; border-radius: 6px; }
.portal-acct__link-row { display: flex; gap: 0.5rem; }
.portal-acct__warn { margin: 0; font: 400 11px/1.4 Inter, sans-serif; color: #b45309; background: #fffbeb; border: 1px solid #fde68a; border-radius: 6px; padding: 6px 8px; }
.portal-acct__error { margin: 0; font: 400 12px Inter, sans-serif; color: #b91c1c; }

.detail-panel__grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
}

.detail-panel__field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.detail-panel__field-label {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.detail-panel__field-value {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
  display: flex;
  align-items: center;
  gap: 0.3rem;
}

.detail-panel__field-value--mono {
  font-family: var(--font-mono);
}

.detail-panel__field-hint {
  font-size: 10px;
  font-weight: 400;
  color: #8a96a8;
}

.detail-panel__field-currency {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
}

.detail-panel__input {
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.3rem 0.5rem;
}

.detail-panel__saved-flash {
  color: #16a34a;
  font-size: 12px;
  font-family: var(--font-body);
}

.detail-panel__tag {
  border-radius: 4px;
  padding: 0.05rem 0.35rem;
  font-size: 10px;
  font-weight: 500;
}

.detail-panel__tag--green { background: #dcfce7; color: #16a34a; }
.detail-panel__tag--amber { background: #fef3c7; color: #d97706; }

.detail-panel__escalations {
  margin-top: 0.75rem;
}

.detail-panel__subtitle {
  font-family: var(--font-body);
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  color: #94a3b8;
}

.detail-panel__escalation-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 32px;
  font-family: var(--font-body);
  font-size: 12px;
}

.detail-panel__escalation-row--next {
  background: #eff6ff;
  border-radius: 6px;
  padding: 0 0.4rem;
}

.detail-panel__escalation-check {
  color: #16a34a;
  font-size: 12px;
}

.detail-panel__escalation-date {
  color: #64748b;
  flex: 1;
}

.detail-panel__escalation-percent {
  color: #16a34a;
  font-weight: 500;
}

.detail-panel__escalation-rent {
  font-family: var(--font-mono);
  color: #1e3a5f;
}

.detail-panel__escalation-clock {
  color: #f0a500;
  font-size: 12px;
}

.detail-panel__vigencia-dates {
  display: flex;
  justify-content: space-between;
}

.detail-panel__vigencia-label {
  font-size: 10px;
  color: #94a3b8;
  font-family: var(--font-body);
}

.detail-panel__vigencia-date {
  font-family: var(--font-body);
  font-size: 14px;
  font-weight: 500;
  color: #1e3a5f;
}

.detail-panel__vigencia-end-wrap {
  text-align: right;
}

.detail-panel__vigencia-date--navy { color: #1e3a5f; }
.detail-panel__vigencia-date--gray { color: #374151; }
.detail-panel__vigencia-date--amber { color: #f0a500; }
.detail-panel__vigencia-date--red { color: #e84545; }
.detail-panel__vigencia-date--green { color: #10b981; }

.detail-panel__progress-wrap {
  margin-top: 0.6rem;
}

.detail-panel__progress-bg {
  height: 6px;
  background: #e2e8f0;
  border-radius: 3px;
  overflow: hidden;
}

.detail-panel__progress-fill {
  height: 100%;
}

.detail-panel__progress-fill--navy { background: #1e3a5f; }
.detail-panel__progress-fill--gray { background: #374151; }
.detail-panel__progress-fill--amber { background: #f0a500; }
.detail-panel__progress-fill--red { background: #e84545; }
.detail-panel__progress-fill--green { background: #10b981; }

.detail-panel__vigencia-text {
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
  margin-top: 0.4rem;
}

.detail-panel__renewal-btn {
  width: 100%;
  margin-top: 0.75rem;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.6rem 0.5rem;
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  line-height: 1.3;
  white-space: normal;
  text-align: center;
  cursor: pointer;
}

.detail-panel__renewal-stepper {
  display: flex;
  gap: 0.3rem;
  margin-top: 0.75rem;
}

.detail-panel__renewal-step {
  flex: 1;
  text-align: center;
  font-family: var(--font-body);
  font-size: 10px;
  padding: 0.3rem;
  border-radius: 5px;
  background: #f1f5f9;
  color: #94a3b8;
}

.detail-panel__renewal-step--current {
  background: #eff6ff;
  color: #3b82f6;
  font-weight: 500;
}

.detail-panel__renewal-step--done {
  background: #dcfce7;
  color: #16a34a;
}

.detail-panel__payments-summary {
  display: flex;
  gap: 2rem;
  margin-bottom: 0.75rem;
}

.detail-panel__summary-number {
  display: block;
  font-family: var(--font-body);
  font-size: 20px;
  font-weight: 500;
}

.detail-panel__summary-number--green { color: #16a34a; }
.detail-panel__summary-number--red { color: #dc2626; }
.detail-panel__summary-number--gray { color: #94a3b8; }
.detail-panel__summary-number--amber { color: #d97706; }

.detail-panel__summary-label {
  font-family: var(--font-body);
  font-size: 11px;
  color: #94a3b8;
}

.detail-panel__payment-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 36px;
  font-family: var(--font-body);
  font-size: 12px;
}

.detail-panel__payment-month {
  flex: 1;
  color: #64748b;
}

.detail-panel__payment-amount {
  font-family: var(--font-mono);
  color: #1e3a5f;
}

.detail-panel__payment-icon--green { color: #16a34a; }
.detail-panel__payment-icon--amber { color: #f0a500; }
.detail-panel__payment-icon--red { color: #dc2626; }
.detail-panel__payment-icon--gray { color: #94a3b8; }

.detail-panel__add-form {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 0.6rem;
  margin-bottom: 0.75rem;
}

.detail-panel__form-submit {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
}

.detail-panel__form-cancel {
  background: none;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.4rem;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  color: #64748b;
}

.detail-panel__contacts-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-panel__contact-card {
  position: relative;
  background: #f8f9fc;
  border-radius: 6px;
  padding: 0.6rem;
}

.detail-panel__contact-role-tag {
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  font-size: 9px;
  padding: 0.05rem 0.3rem;
  border-radius: 4px;
  font-family: var(--font-body);
}

.detail-panel__contact-role-tag--legal { background: #1e3a5f; color: #fff; }
.detail-panel__contact-role-tag--operational { background: #dbeafe; color: #3b82f6; }
.detail-panel__contact-role-tag--payments { background: #dcfce7; color: #16a34a; }
.detail-panel__contact-role-tag--emergency { background: #fef3c7; color: #d97706; }

.detail-panel__contact-name {
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  color: #1e3a5f;
}

.detail-panel__contact-role {
  font-family: var(--font-body);
  font-size: 11px;
  color: #64748b;
}

.detail-panel__contact-line {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  font-family: var(--font-body);
  font-size: 12px;
  color: #1e3a5f;
  margin-top: 0.2rem;
}

.detail-panel__copy-icon {
  font-size: 12px;
  color: #94a3b8;
  cursor: pointer;
}

.detail-panel__contact-notes {
  font-style: italic;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 0.3rem;
}

.detail-panel__dropzone {
  border: 1px dashed #cbd5e1;
  background: #f8f9fc;
  border-radius: 8px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  color: #94a3b8;
  font-family: var(--font-body);
  font-size: 12px;
  margin-bottom: 0.6rem;
}

.detail-panel__documents-list {
  display: flex;
  flex-direction: column;
}

.detail-panel__document-row {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  height: 40px;
}

.detail-panel__document-icon {
  color: #dc2626;
  font-size: 18px;
}

.detail-panel__document-main {
  flex: 1;
}

.detail-panel__document-name {
  font-family: var(--font-body);
  font-size: 13px;
  color: #1e3a5f;
}

.detail-panel__document-tag {
  font-size: 10px;
  color: #64748b;
  background: #f1f5f9;
  border-radius: 4px;
  padding: 0.05rem 0.3rem;
}

.detail-panel__document-date {
  font-size: 10px;
  color: #94a3b8;
}

.detail-panel__notes-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 0.6rem;
}

.detail-panel__note-bubble {
  background: #f8f9fc;
  border-radius: 8px;
  padding: 10px;
}

.detail-panel__note-text {
  font-family: var(--font-body);
  font-size: 13px;
  color: #1e3a5f;
}

.detail-panel__note-meta {
  font-family: var(--font-body);
  font-size: 10px;
  color: #94a3b8;
  margin-top: 0.3rem;
}

.detail-panel__note-input {
  width: 100%;
  font-family: var(--font-body);
  font-size: 13px;
  color: var(--text-content);
  background: #f8f9fc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 0.5rem;
  resize: none;
  box-sizing: border-box;
  display: block;
}

.detail-panel__note-actions {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.4rem;
}

.detail-panel__note-save {
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  padding: 0.4rem 0.75rem;
  font-family: var(--font-body);
  font-size: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.detail-panel__audit-list {
  display: flex;
  flex-direction: column;
}

.detail-panel__audit-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  height: 32px;
  font-family: var(--font-body);
  font-size: 12px;
}

.detail-panel__audit-row--significant {
  background: #f8f9fc;
  border-radius: 4px;
  padding: 0 0.4rem;
}

.detail-panel__audit-icon {
  font-size: 12px;
  color: #94a3b8;
}

.detail-panel__audit-text {
  flex: 1;
  color: #1e3a5f;
}

.detail-panel__audit-meta {
  font-size: 10px;
  color: #94a3b8;
}

.detail-panel__audit-empty {
  font-family: var(--font-body);
  font-size: 12px;
  color: #94a3b8;
  padding: 0.5rem 0;
}

.detail-panel__actions {
  flex-shrink: 0;
  height: 56px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem;
  border-top: 1px solid #e2e8f0;
}

.detail-panel__action-primary {
  flex: 0 0 45%;
  background: #3b82f6;
  color: #ffffff;
  border: none;
  border-radius: 6px;
  height: 36px;
  font-family: var(--font-body);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
}

.detail-panel__action-secondary {
  flex: 0 0 40%;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  height: 36px;
  color: #1e3a5f;
  font-family: var(--font-body);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
}

.detail-panel__more-wrap {
  position: relative;
  flex: 1;
  display: flex;
  justify-content: flex-end;
}

.detail-panel__more-btn {
  background: none;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  display: flex;
}

.detail-panel__more-menu {
  position: absolute;
  bottom: 100%;
  right: 0;
  margin-bottom: 4px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  min-width: 220px;
  padding: 0.3rem;
  z-index: 50;
}

.detail-panel__more-menu button {
  display: block;
  width: 100%;
  text-align: left;
  background: none;
  border: none;
  font-family: var(--font-body);
  font-size: 12px;
  color: var(--text-content);
  padding: 0.4rem 0.5rem;
  cursor: pointer;
  border-radius: 5px;
}

.detail-panel__more-menu button:hover {
  background: #f8fafc;
}

.detail-panel__payment-form {
  position: absolute;
  bottom: 56px;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
  padding: 0.75rem 1rem;
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.detail-panel__renewal-advance-btn {
  width: 100%;
  margin-top: 0.5rem;
  background: #eff6ff;
  color: #3b82f6;
  border: 1px solid #bfdbfe;
  border-radius: 6px;
  padding: 0.5rem;
  font-family: var(--font-body);
  font-size: 10px;
  font-weight: 500;
  line-height: 1.3;
  white-space: normal;
  text-align: center;
  cursor: pointer;
}

.detail-panel__renewal-confirm-form {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.5rem;
}

.detail-panel__contact-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 0.4rem;
}

.detail-panel__contact-action-btn {
  background: none;
  border: none;
  color: #3b82f6;
  font-family: var(--font-body);
  font-size: 11px;
  cursor: pointer;
  padding: 0;
}

.detail-panel__contact-action-btn--danger {
  color: #dc2626;
}

.detail-panel__document-action {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 14px;
  cursor: pointer;
  text-decoration: none;
  padding: 0;
}

.detail-panel__document-action:hover {
  color: #1e3a5f;
}

.detail-panel__document-action--danger:hover {
  color: #dc2626;
}

/* Formulario de pago: apilado en vez de una sola fila */
.detail-panel__payment-form {
  position: absolute;
  bottom: 56px;
  left: 0;
  right: 0;
  background: #ffffff;
  border-top: 1px solid #e2e8f0;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.08);
  padding: 0.75rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.detail-panel__payment-form-fields {
  display: flex;
  gap: 0.5rem;
}

.detail-panel__payment-form-fields .detail-panel__input {
  flex: 1;
}

.detail-panel__payment-form-actions {
  display: flex;
  gap: 0.5rem;
}

.detail-panel__payment-form-actions .detail-panel__form-submit,
.detail-panel__payment-form-actions .detail-panel__form-cancel {
  flex: 1;
}
.detail-panel__action-error { margin: 0.4rem 1rem 0; font-size: 12px; color: #dc2626; }
</style>